import { ActionModal, Checkbox, FormError } from '@kcftech/design-system';
import {
    IMachineHealthNotificationSubscriptionResponse,
    IMachineHealthNotificationTriggerRequestChild,
    IMachineHealthNotificationTriggerResponseChild,
    MachineHealthEventType,
} from '@kcftech/prometheus-client';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useEditMachineHealthNotificationSubscription } from 'custom_hooks/data_hooks/notification_subscriptions/useEditMachineHealthNotificationSubscription';
import { DeskNotificationTriggerTable } from 'pages/Settings/NotificationSubscriptions/DeskNotificationTriggerTable';
import { EditMachineHealthNotificationSubscriptionFormData } from 'pages/Settings/NotificationSubscriptions/form/types';
import { NotificationTriggerTableWrapper } from 'pages/Settings/NotificationSubscriptions/NotificationTriggerTable';
import {
    hasEnabledNotificationMethods,
    machineHealthEventTypeName,
    validateNotificationTriggers,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
    Controller,
    UseFormReturn,
    useForm,
    useWatch,
    useFormState,
} from 'react-hook-form';
import { toMap } from 'utils/array';

export type EditMachineHealthNotificationSubscriptionModalProps = Readonly<{
    notificationSubscription: IMachineHealthNotificationSubscriptionResponse;
    onClose: (saved: boolean) => void;
}>;

const responseToFormData = (
    response: IMachineHealthNotificationSubscriptionResponse,
): EditMachineHealthNotificationSubscriptionFormData => ({
    isSilenced: response.IsSilenced,
    notificationTriggers: Object.fromEntries(
        withMissingDeskTriggers(response.NotificationTriggers),
    ) as {
        [key in MachineHealthEventType]: IMachineHealthNotificationTriggerRequestChild;
    },
});

/**
 * A Subscription doesn't _necessarily_ contain all possible triggers. We
 * display all possibilities with checkboxes in the UI, though. This adds the
 * necessary triggers as needed (They will later be removed before we serialize
 * the request, if unused)
 */
const withMissingDeskTriggers = (
    existing: IMachineHealthNotificationTriggerResponseChild[],
): [MachineHealthEventType, IMachineHealthNotificationTriggerRequestChild][] => {
    const existingByEventType = toMap(
        existing,
        (v) => v.MachineHealthEventType,
        (v) => ({
            ...v,
            MachineHealthEventType: v.MachineHealthEventType,
        }),
    );

    return [
        ...existingByEventType.entries(),
        ...Object.values(MachineHealthEventType)
            .filter((eventType) => existingByEventType.get(eventType) === undefined)
            .map<[MachineHealthEventType, IMachineHealthNotificationTriggerRequestChild]>(
                (eventType) => [
                    eventType,
                    {
                        MachineHealthEventType: eventType,
                        IsSdConnectPushEnabled: false,
                        IsEmailEnabled: false,
                        IsSmsEnabled: false,
                    },
                ],
            ),
    ];
};

export function EditMachineHealthNotificationSubscriptionModal({
    notificationSubscription,
    onClose,
}: EditMachineHealthNotificationSubscriptionModalProps) {
    const { showToast } = useNotificationContext();
    const notificationTriggerErrorState = useState<string | undefined>(undefined);

    const form = useForm<EditMachineHealthNotificationSubscriptionFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: false,
        defaultValues: responseToFormData(notificationSubscription),
    });

    const {
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
    } = form;

    const {
        mutate: editMachineHealthNotificationSubscription,
        isPending: isEditingMachineHealth,
    } = useEditMachineHealthNotificationSubscription({
        onSuccess: () => {
            onClose(true);
            showToast({
                type: 'success',
                content: 'Successfully updated machine health notification subscription',
            });
        },
        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update machine health notification subscription',
            }),
    });

    const handleEdit = handleSubmit((formData) => {
        if (
            validateNotificationTriggers(Object.values(formData.notificationTriggers)) !==
            undefined
        ) {
            return; // this is an error, handled by notificationTriggerErrorState
        }
        editMachineHealthNotificationSubscription({
            id: notificationSubscription.Id,
            isSilenced: formData.isSilenced,
            triggers: Object.values(formData.notificationTriggers).filter(
                (nt) => !!nt.NotificationTriggerId || hasEnabledNotificationMethods(nt),
            ),
        });
    });

    return (
        <ActionModal
            title="Edit Machine Health Notification Subscription"
            actionable={
                (!isSubmitted || (isValid && !notificationTriggerErrorState[0])) &&
                !(isValidating || isSubmitting || isEditingMachineHealth)
            }
            onAction={handleEdit}
            onClose={() => onClose(false)}
            actionButtonText="Save"
            closeButtonText="Cancel"
            height={384}
        >
            <MachineHealthAlertsTriggersEditor
                form={form}
                errorState={notificationTriggerErrorState}
            />
        </ActionModal>
    );
}

function MachineHealthAlertsTriggersEditor({
    form,
    errorState,
}: {
    form: UseFormReturn<EditMachineHealthNotificationSubscriptionFormData>;
    errorState: [string | undefined, Dispatch<SetStateAction<string | undefined>>];
}) {
    const {
        control,
    } = form;

    const {
        isSubmitted,
    } = useFormState({
        control,
    });

    const notificationTriggers = useWatch({ control, name: 'notificationTriggers' });
    const { canViewMachineHealthNotificationSubscriptionCategory } = usePermissions();

    const [error, setError] = errorState;

    useEffect(() => {
        setError(validateNotificationTriggers(Object.values(notificationTriggers)));
    }, [notificationTriggers, setError]);

    const rowForEventType = (eventType: MachineHealthEventType) => (
        <tr key={eventType}>
            <td>{machineHealthEventTypeName[eventType]}</td>
            <td className="text-right">
                <Controller
                    control={control}
                    name={`notificationTriggers.${eventType}.IsEmailEnabled` as const}
                    render={({ field: { value, onChange } }) => (
                        <Checkbox
                            label=""
                            onChange={onChange}
                            checked={value}
                            title={`Toggle emails for ${machineHealthEventTypeName[eventType]}`}
                        />
                    )}
                />
            </td>
            <td className="text-right">
                <Controller
                    control={control}
                    name={
                        `notificationTriggers.${eventType}.IsSdConnectPushEnabled` as const
                    }
                    render={({ field: { value, onChange } }) => (
                        <Checkbox
                            label=""
                            onChange={onChange}
                            disabled={eventType === MachineHealthEventType.IssueActivity}
                            checked={value}
                            title={`${
                                eventType !== MachineHealthEventType.IssueActivity
                                    ? 'Toggle push notifications'
                                    : 'push notifications unavailable'
                            } for ${machineHealthEventTypeName[eventType]}`}
                        />
                    )}
                />
            </td>
        </tr>
    );

    return (
        <NotificationTriggerTableWrapper>
            <DeskNotificationTriggerTable
                rows={Object.values(MachineHealthEventType)
                    .filter(canViewMachineHealthNotificationSubscriptionCategory)
                    .map(rowForEventType)}
            />
            <FormError message={isSubmitted ? error : undefined} />
        </NotificationTriggerTableWrapper>
    );
}
